import {
    forwardRef,
    FunctionComponent,
    MouseEventHandler,
    useCallback,
    useImperativeHandle,
    useState,
} from "react"
import { TriCheckbox, TriCheckboxState } from "@nova/component-checkbox"
import * as Icons from "@nova/component-icons"
import { InputOutlined } from "@nova/ds-component-input"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { PilotTrackingTag, PilotTrackingTagMissionSpec } from "@nova/schemas"
import { useCreateTag, useDeleteTag, useUpdateTag, useTags } from "../tags/TagQueries"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { DeleteTagModal } from "../tags/DeleteTagModal"
import { usePendingTagState } from "./usePendingTagState"
import { MAX_MISSION_TAG_LENGTH_CHARS } from "@nova/constants"
import { IconButton } from "@nova/component-btn"
import { TitleInput } from "../TitleInput"

export type MissionTagDropdownPanelRef = {
    commit: () => void
}

type Props = {
    missions: { missionId: string; tagIds: string[] }[]
    onCommit: (
        additions: PilotTrackingTagMissionSpec[],
        deletions: PilotTrackingTagMissionSpec[]
    ) => void
    onRequestClose?: () => void
}

export const MissionTagDropdownPanel = forwardRef<MissionTagDropdownPanelRef, Props>(
    ({ missions, onCommit, onRequestClose }, ref) => {
        const [search, setSearch] = useState("")

        const { data: allTags = [] } = useTags()
        const { createTag } = useCreateTag()
        const { updateTag } = useUpdateTag()
        const { deleteTag } = useDeleteTag()
        const modal = useConfirmationModalContext()
        const { getTagState, toggleTag, buildDiff } = usePendingTagState(missions)

        const commit = useCallback(() => {
            const { additions, deletions } = buildDiff()
            onCommit(additions, deletions)
        }, [buildDiff, onCommit])

        useImperativeHandle(ref, () => ({ commit }), [commit])

        const handleCreate = useCallback(async () => {
            const name = search.trim()
            if (!name) return
            const newTag = await createTag({ name })
            if (newTag) {
                toggleTag(newTag.id)
                setSearch("")
            }
        }, [search, createTag, toggleTag])

        const filtered = allTags.filter(
            (t) => !search || t.name.toLowerCase().includes(search.toLowerCase())
        )
        const sortedFiltered = (state: "applied" | "partial" | "not-applied") =>
            filtered
                .filter((t) => getTagState(t.id) === state)
                .sort((a, b) => a.name.localeCompare(b.name))

        const sections = [
            { label: "Applied", state: "checked" as const, tags: sortedFiltered("applied") },
            {
                label: "Partially Applied",
                state: "indeterminate" as const,
                tags: sortedFiltered("partial"),
            },
            {
                label: "Previously Used",
                state: "unchecked" as const,
                tags: sortedFiltered("not-applied"),
            },
        ]

        const showCreate =
            search.trim().length > 0 &&
            !allTags.some((t) => t.name.toLowerCase() === search.trim().toLowerCase())

        return (
            <div className="bg-gray-700 rounded-xl min-w-[270px] overflow-hidden">
                <div className="px-4 pt-2 pb-1 text-white">
                    <InputOutlined
                        placeholder="Search and create tags"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.currentTarget.value.slice(0, MAX_MISSION_TAG_LENGTH_CHARS))
                        }}
                        leftItem={<Icons.SmallIcon Icon={Icons.MagnifyingGlass} />}
                    />
                </div>
                <ScrollableArea className="max-h-64">
                    {sections.map(({ label, state, tags }) =>
                        tags.length > 0 ? (
                            <div key={label}>
                                <small className="xs text-gray-400 px-3">{label}</small>
                                {tags.map((tag) => (
                                    <TagListItem
                                        key={tag.id}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleTag(tag.id)
                                        }}
                                        onDelete={async () => {
                                            onRequestClose?.()
                                            const confirmed = await modal.confirm({
                                                RenderComponent: ({ cancel, confirm }) => (
                                                    <DeleteTagModal
                                                        onCancel={cancel}
                                                        onSubmit={confirm}
                                                    />
                                                ),
                                            })
                                            if (confirmed) void deleteTag({ tagId: tag.id })
                                        }}
                                        onRename={(name) => void updateTag({ tagId: tag.id, name })}
                                        state={state}
                                        tag={tag}
                                    />
                                ))}
                            </div>
                        ) : null
                    )}
                    {showCreate && (
                        <>
                            <p className="text-[10px] uppercase tracking-widest text-gray-400 px-3 pt-2 pb-1">
                                Create
                            </p>
                            <div
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-600 cursor-pointer"
                                onClick={() => void handleCreate()}
                            >
                                <Icons.SmallIcon Icon={Icons.Plus} className="text-gray-400" />
                                <span className="text-sm text-white">{search.trim()}</span>
                            </div>
                        </>
                    )}
                </ScrollableArea>
            </div>
        )
    }
)

interface TagListItemProps {
    tag: PilotTrackingTag
    onRename: (name: string) => void
    onDelete: () => void
    onClick: MouseEventHandler<HTMLDivElement>
    state: TriCheckboxState
}

const TagListItem: FunctionComponent<TagListItemProps> = ({
    onDelete,
    onRename,
    tag,
    state,
    onClick,
}) => {
    const [isEditing, setIsEditing] = useState(false)

    return (
        <div
            key={tag.id}
            className="flex items-center justify-between px-3 py-2 hover:bg-gray-600 cursor-pointer group/tag"
            onClick={(e) => {
                e.stopPropagation()

                if (!isEditing) {
                    onClick(e)
                }
            }}
        >
            {isEditing ? (
                <div
                    className="flex items-center gap-1 w-full"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <TitleInput
                        className="flex-1"
                        initialValue={tag.name}
                        onSubmit={(name) => {
                            onRename(name)
                            setIsEditing(false)
                        }}
                        onCancel={() => setIsEditing(false)}
                        autoFocus
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                    <IconButton
                        className="text-gray-400 hover:!text-red-500"
                        onClick={() => {
                            onDelete()
                            setIsEditing(false)
                        }}
                    >
                        <Icons.SmallIcon Icon={Icons.Trash} />
                    </IconButton>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-3">
                        <TriCheckbox state={state} />
                        <span className="text-sm text-white">{tag.name}</span>
                    </div>

                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsEditing(true)
                        }}
                        className="text-gray-100 opacity-0 group-hover/tag:opacity-100 justify-self-end"
                    >
                        <Icons.SmallIcon Icon={Icons.Edit} />
                    </IconButton>
                </>
            )}
        </div>
    )
}

MissionTagDropdownPanel.displayName = "MissionTagDropdownPanel"
